import React, { useEffect, useState, useCallback } from 'react';
import { UserPlus, Check, X, Clock, Mail, Users, Shield, ChevronDown } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';

const ALL_PERMISSIONS = [
  { key: 'dashboard', label: 'Tableau de bord' },
  { key: 'employees', label: 'Salariés' },
  { key: 'vehicles', label: 'Véhicules' },
  { key: 'map', label: 'Carte' },
  { key: 'planning', label: 'Planning' },
  { key: 'tasks', label: 'Tâches' },
  { key: 'incidents', label: 'Incidents' },
  { key: 'warnings', label: 'Avertissements' },
  { key: 'maintenance', label: 'Maintenance' },
  { key: 'messages', label: 'Messages' },
  { key: 'settings', label: 'Paramètres' },
  { key: 'admin', label: 'Administration' },
];

const ROLE_DEFAULTS = {
  admin: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'admin', 'settings', 'messages'],
  rh: ['dashboard', 'employees', 'planning', 'tasks', 'incidents', 'warnings', 'settings', 'messages'],
  responsable: ['dashboard', 'employees', 'vehicles', 'map', 'planning', 'tasks', 'incidents', 'warnings', 'settings', 'messages'],
  chauffeur: ['dashboard', 'planning', 'tasks', 'incidents', 'settings', 'messages'],
};

const ROLES = [
  { key: 'chauffeur', label: 'Chauffeur' },
  { key: 'responsable', label: 'Responsable' },
  { key: 'rh', label: 'Ressources Humaines' },
  { key: 'admin', label: 'Administrateur' },
];

// ===== Approval Modal =====
const ApproveModal = ({ user, onClose, onConfirm, loading, theme }) => {
  const [role, setRole] = useState('chauffeur');
  const [permissions, setPermissions] = useState(ROLE_DEFAULTS['chauffeur']);

  const handleRoleChange = (newRole) => {
    setRole(newRole);
    setPermissions(ROLE_DEFAULTS[newRole] || []);
  };

  const togglePermission = (key) => {
    setPermissions(prev =>
      prev.includes(key) ? prev.filter(p => p !== key) : [...prev, key]
    );
  };

  const selectAll = () => setPermissions(ALL_PERMISSIONS.map(p => p.key));
  const deselectAll = () => setPermissions([]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="approve-modal">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={cn(
        'relative w-full max-w-lg rounded-2xl border p-6 max-h-[90vh] overflow-y-auto',
        theme === 'dark' ? 'bg-zinc-900 border-white/10' : 'bg-white border-gray-200 shadow-xl'
      )}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className={cn(
              'w-10 h-10 rounded-xl flex items-center justify-center',
              theme === 'dark' ? 'bg-green-500/15' : 'bg-green-100'
            )}>
              <Shield size={20} className="text-green-500" />
            </div>
            <div>
              <h2 className={cn('text-lg font-bold', theme === 'dark' ? 'text-white' : 'text-gray-900')}>
                Approuver {user.prenom} {user.nom}
              </h2>
              <p className={cn('text-xs', theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
                {user.email}
              </p>
            </div>
          </div>
          <button onClick={onClose} className={cn(
            'p-2 rounded-lg transition-colors',
            theme === 'dark' ? 'hover:bg-white/10 text-zinc-400' : 'hover:bg-gray-100 text-gray-400'
          )}>
            <X size={20} />
          </button>
        </div>

        {/* Role selector */}
        <div className="mb-5">
          <label className={cn('text-xs font-semibold uppercase tracking-wider mb-2 block', theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
            Rôle
          </label>
          <div className="grid grid-cols-2 gap-2">
            {ROLES.map(r => (
              <button
                key={r.key}
                onClick={() => handleRoleChange(r.key)}
                className={cn(
                  'px-3 py-2.5 rounded-xl text-sm font-medium border transition-all text-left',
                  role === r.key
                    ? (theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-900 border-gray-900 text-white')
                    : (theme === 'dark' ? 'border-white/5 text-zinc-500 hover:border-white/15 hover:text-zinc-300' : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700')
                )}
                data-testid={`role-${r.key}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Permissions */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className={cn('text-xs font-semibold uppercase tracking-wider', theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
              Permissions ({permissions.length}/{ALL_PERMISSIONS.length})
            </label>
            <div className="flex gap-2">
              <button onClick={selectAll} className={cn('text-xs px-2 py-1 rounded transition-colors', theme === 'dark' ? 'text-zinc-500 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100')}>
                Tout
              </button>
              <button onClick={deselectAll} className={cn('text-xs px-2 py-1 rounded transition-colors', theme === 'dark' ? 'text-zinc-500 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100')}>
                Aucun
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {ALL_PERMISSIONS.map(p => (
              <button
                type="button"
                key={p.key}
                onClick={() => togglePermission(p.key)}
                className={cn(
                  'flex items-center gap-2.5 px-3 py-2.5 rounded-xl border cursor-pointer transition-all text-left',
                  permissions.includes(p.key)
                    ? (theme === 'dark' ? 'bg-green-500/10 border-green-500/30 text-green-400' : 'bg-green-50 border-green-300 text-green-700')
                    : (theme === 'dark' ? 'border-white/5 text-zinc-500 hover:border-white/10' : 'border-gray-200 text-gray-400 hover:border-gray-300')
                )}
                data-testid={`perm-${p.key}`}
              >
                <div className={cn(
                  'w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border',
                  permissions.includes(p.key)
                    ? 'bg-green-500 border-green-500'
                    : (theme === 'dark' ? 'border-white/20' : 'border-gray-300')
                )}>
                  {permissions.includes(p.key) && <Check size={12} className="text-white" />}
                </div>
                <span className="text-sm">{p.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button
            onClick={() => onConfirm(user.id, role, permissions)}
            disabled={loading || permissions.length === 0}
            className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white"
            data-testid="confirm-approve"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Approbation...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Check size={18} />
                Confirmer l'approbation
              </span>
            )}
          </Button>
          <Button variant="outline" onClick={onClose} disabled={loading}
            className={cn('h-11', theme === 'dark' ? 'border-white/10 text-zinc-400 hover:bg-white/5' : 'border-gray-200 text-gray-600')}>
            Annuler
          </Button>
        </div>
      </div>
    </div>
  );
};

// ===== User Card =====
const PendingUserCard = ({ user, onApprove, onReject, loading, theme }) => {
  const timeAgo = () => {
    if (!user.created_at) return '';
    const diff = Date.now() - new Date(user.created_at).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `Il y a ${mins}min`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `Il y a ${hours}h`;
    return `Il y a ${Math.floor(hours / 24)}j`;
  };

  return (
    <div className={cn(
      'p-5 rounded-xl border transition-all',
      theme === 'dark' ? 'bg-zinc-900/60 border-white/10 hover:border-white/20' : 'bg-white border-gray-200 hover:border-gray-300 shadow-sm'
    )} data-testid={`pending-user-${user.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <div className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 text-lg font-bold',
            theme === 'dark' ? 'bg-amber-500/15 text-amber-400' : 'bg-amber-100 text-amber-600'
          )}>
            {(user.prenom?.[0] || '').toUpperCase()}{(user.nom?.[0] || '').toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={cn('text-base font-semibold', theme === 'dark' ? 'text-white' : 'text-gray-900')}>
              {user.prenom} {user.nom}
            </h3>
            <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 mt-1">
              <span className={cn('text-sm flex items-center gap-1.5', theme === 'dark' ? 'text-zinc-400' : 'text-gray-500')}>
                <Mail size={14} /> {user.email}
              </span>
              <span className={cn('text-xs flex items-center gap-1.5', theme === 'dark' ? 'text-zinc-500' : 'text-gray-400')}>
                <Clock size={12} /> {timeAgo()}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button size="sm" onClick={() => onApprove(user)} disabled={loading}
            className="h-9 px-4 bg-green-600 hover:bg-green-700 text-white" data-testid={`approve-${user.id}`}>
            <Check size={16} className="mr-1.5" /> Approuver
          </Button>
          <Button size="sm" variant="outline" onClick={() => onReject(user.id)} disabled={loading}
            className={cn('h-9 px-4', theme === 'dark' ? 'border-red-500/30 text-red-400 hover:bg-red-500/10' : 'border-red-200 text-red-600 hover:bg-red-50')}
            data-testid={`reject-${user.id}`}>
            <X size={16} className="mr-1.5" /> Refuser
          </Button>
        </div>
      </div>
    </div>
  );
};

// ===== Main Page =====
export default function PendingUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [approveUser, setApproveUser] = useState(null);
  const { theme } = useTheme();

  const fetchPendingUsers = useCallback(async () => {
    try {
      const res = await api.get('/admin/pending-users');
      setUsers(res.data || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPendingUsers(); }, [fetchPendingUsers]);

  const handleConfirmApprove = async (userId, role, permissions) => {
    setActionLoading(true);
    try {
      await api.post(`/admin/pending-users/${userId}/approve`, { role, permissions });
      setUsers(prev => prev.filter(u => u.id !== userId));
      setApproveUser(null);
      toast.success('Utilisateur approuvé avec succès');
    } catch (error) {
      toast.error("Erreur lors de l'approbation");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!window.confirm(`Refuser et supprimer le compte de ${user?.prenom} ${user?.nom} ?`)) return;
    setActionLoading(true);
    try {
      await api.post(`/admin/pending-users/${userId}/reject`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Utilisateur refusé et supprimé');
    } catch (error) {
      toast.error('Erreur lors du refus');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="relative">
          <div className={cn('w-16 h-16 border-4 rounded-full', theme === 'dark' ? 'border-zinc-800' : 'border-gray-200')}></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-transparent border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-1 md:p-0 pb-24 md:pb-0" data-testid="pending-users-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className={cn('w-12 h-12 rounded-xl flex items-center justify-center', theme === 'dark' ? 'bg-amber-500/20' : 'bg-amber-100')}>
          <UserPlus size={24} className={theme === 'dark' ? 'text-amber-400' : 'text-amber-600'} />
        </div>
        <div>
          <h1 className={cn('text-xl font-bold uppercase tracking-wide', theme === 'dark' ? 'text-white' : 'text-gray-900')}>
            Nouveaux Utilisateurs
          </h1>
          <p className={cn('text-sm', theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>
            {users.length > 0 ? `${users.length} en attente d'approbation` : "Aucune demande en attente"}
          </p>
        </div>
      </div>

      {/* List */}
      {users.length > 0 ? (
        <div className="space-y-3">
          {users.map(user => (
            <PendingUserCard key={user.id} user={user} onApprove={setApproveUser} onReject={handleReject} loading={actionLoading} theme={theme} />
          ))}
        </div>
      ) : (
        <div className={cn('p-12 text-center rounded-xl border', theme === 'dark' ? 'bg-zinc-900/50 border-white/10' : 'bg-white border-gray-200')}>
          <div className={cn('w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4', theme === 'dark' ? 'bg-zinc-800' : 'bg-gray-100')}>
            <Users size={32} className={theme === 'dark' ? 'text-zinc-500' : 'text-gray-400'} />
          </div>
          <h3 className={cn('text-lg font-semibold mb-2', theme === 'dark' ? 'text-white' : 'text-gray-900')}>Aucune demande</h3>
          <p className={cn('text-sm', theme === 'dark' ? 'text-zinc-500' : 'text-gray-500')}>Tous les nouveaux comptes ont été traités.</p>
        </div>
      )}

      {/* Approve Modal */}
      {approveUser && (
        <ApproveModal
          user={approveUser}
          onClose={() => setApproveUser(null)}
          onConfirm={handleConfirmApprove}
          loading={actionLoading}
          theme={theme}
        />
      )}
    </div>
  );
}
